import type { ReadonlyURLSearchParams } from 'next/navigation'
import { isStocksFunnel } from '../common/web/is-stocks-host'
import { logNewRelicEvent } from '../common/custom/newrelic-page-events'
import { getURL } from '../url'

import { isDSPIMFunnel, isEdelweissMFFunnel } from '../common/web/is-external-host'
import { getDomainUrl, getINDStockDomainUrl } from '@/root/api/url-constants'
import type { IDecryptRequestBody } from '@/root/types/auth-api'
import { DSPIM_DOMAIN, EDELWEISSMF_DOMAIN } from '@/root/constants/common'

export const getReturnTo = (
  pathname: string,
  searchParams: ReadonlyURLSearchParams | string,
) => {
  if (typeof searchParams === 'string' && searchParams.startsWith('?')) {
    searchParams = searchParams.slice(1)
  }

  const currentUrl = searchParams.toString()
    ? `${pathname}?${searchParams.toString()}`
    : pathname

  return encodeURIComponent(currentUrl)
}

// Post login function tailored for STENCIL
export const onLogin = (
  returnTo = '',
  verifyUserData: IDecryptRequestBody,
  isNew?: boolean,
  isLoginFlow?: boolean,
) => {
  const { hostname } = window.location

  const indmoneyDomain = getDomainUrl()

  /** domain of destination */
  const targetDomain = isStocksFunnel()
    ? indmoneyDomain
    : isDSPIMFunnel(hostname)
      ? DSPIM_DOMAIN
      : isEdelweissMFFunnel(hostname)
        ? EDELWEISSMF_DOMAIN
        : getINDStockDomainUrl()

  /** domain of source */
  const sourceDomain = hostname
    ? `https://${hostname}`
    : isStocksFunnel()
      ? getINDStockDomainUrl()
      : indmoneyDomain

  const logPayload = {
    authCodePresent: !!verifyUserData.auth_code,
    traceId: verifyUserData.trace_id ?? '',
    targetDomain,
  }

  try {
    let returnToPath =
      returnTo && returnTo !== 'about:blank' ? decodeURIComponent(returnTo) : ''

    logNewRelicEvent('web_login_redirect_attempt', logPayload)
    // handling for new user to redirect them to onboarding flow
    if (isNew) {
      returnToPath = '/onboarding-l1-sl1'
    }

    logNewRelicEvent('web_login_redirect_returnto', {
      return_to: returnToPath,
      ...logPayload,
    })

    window.location.href = getURL(`${targetDomain}/sso-handler`, {
      return_to: encodeURIComponent(`${sourceDomain}${returnToPath}`),
      ...(isLoginFlow ? { is_login_flow: 'true' } : {}),
      ...verifyUserData,
    }).toString()
  } catch (error: unknown) {
    logNewRelicEvent('web_login_redirect_failure', {
      error: (error as Error)?.message || 'Unknown error',
      targetDomain,
    })

    window.location.href = getURL(`${targetDomain}/sso-handler`, {
      ...(verifyUserData as unknown as Record<string, string>),
      ...(isLoginFlow ? { is_login_flow: 'true' } : {}),
    }).toString()
  }
}
